import React, { useState, useEffect } from "react";
import { FaStar, FaTrophy, FaExclamationTriangle, FaCheckCircle, FaClock,
         FaChevronDown, FaChevronUp, FaFire, FaBalanceScale } from "react-icons/fa";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
  LineChart, Line, PieChart, Pie, Cell, AreaChart, Area,
} from "recharts";
import api from "./api";
import useAuth from "../hooks/useAuth";
import "./MonthlyDashboard.css";

const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#14b8a6", "#f97316"];

const MonthlyDashboard = () => {
  const [dd, setDD] = useState(null);
  const { auth } = useAuth();
  const [expandedUsers, setExpandedUsers] = useState({});
  const [tasksPerUser, setTasksPerUser] = useState({});
  const [loadingTasks, setLoadingTasks] = useState(false);

  useEffect(() => {
    api.get(`${process.env.REACT_APP_URL}/tasks/monthlydashboard?_=${Date.now()}`)
      .then(r => setDD(r.data)).catch(console.error);
  }, []);

  if (!dd) return <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Loading dashboard...</div>;

  const { uniqueUsers, summary, weeklyData, lineData, statusTrend,
          urgencyBreakdown, typeBreakdown, avgCompletionDays, worthData,
          overdueTasks, createdByBreakdown, checklistSummary } = dd;

  // ─── System totals ──────────────────────────────────────────────
  const sys = Object.values(summary).reduce((a, u) => ({
    completed: a.completed + u.completed, pending: a.pending + u.pending,
    late: a.late + u.late, redStars: a.redStars + u.redStars, greenStars: a.greenStars + u.greenStars,
  }), { completed: 0, pending: 0, late: 0, redStars: 0, greenStars: 0 });

  const totalTasks = sys.completed + sys.pending;
  const completionRate = totalTasks > 0 ? Math.round((sys.completed / totalTasks) * 100) : 0;

  const totalWorthDone = Object.values(worthData).reduce((a, w) => a + w.done, 0);
  const totalWorthPending = Object.values(worthData).reduce((a, w) => a + w.pending, 0);

  // ─── Rankings ───────────────────────────────────────────────────
  const userRankings = uniqueUsers.map(user => {
    const s = summary[user] || { completed: 0, pending: 0, late: 0, redStars: 0, greenStars: 0 };
    const total = s.completed + s.pending;
    const rate = total > 0 ? Math.round((s.completed / total) * 100) : 0;
    const score = s.greenStars * 10 - s.redStars * 10 + s.completed * 5 - s.late * 8;
    const w = worthData[user] || { done: 0, pending: 0, onTime: 0, lateCompleted: 0 };
    const avg = avgCompletionDays[user];
    return { user, ...s, total, rate, score, ...w, avgDays: avg };
  }).sort((a, b) => b.score - a.score);

  const getPerf = (s) => {
    if (s.redStars >= 10 || s.late >= 3) return { label: 'Needs Attention', color: '#dc2626', bg: '#fee2e2' };
    if (s.redStars >= 5 || s.late >= 1) return { label: 'Average', color: '#d97706', bg: '#fef3c7' };
    return { label: 'Good', color: '#059669', bg: '#d1fae5' };
  };

  const getStatusColor = (status) => {
    switch (status) { case "completed": return "#10b981"; case "pending": return "#f59e0b"; case "delayed": return "#ef4444"; default: return "#3b82f6"; }
  };

  const toggleExpand = async (user) => {
    const isExp = expandedUsers[user];
    if (!isExp) {
      setLoadingTasks(true);
      try {
        const res = await api.get(`${process.env.REACT_APP_URL}/tasks/today/dashboard/Task/${user}`);
        setTasksPerUser(p => ({ ...p, [user]: res.data || [] }));
      } catch (e) { console.error(e); }
      finally { setLoadingTasks(false); }
    }
    setExpandedUsers(p => ({ ...p, [user]: !isExp }));
  };

  // ─── Urgency pie data (system-wide) ────────────────────────────
  const urgencyTotals = (urgencyBreakdown || []).reduce((a, u) => {
    ['Urgent', 'Important', 'Scheduled', 'Some Day'].forEach(k => { a[k] = (a[k] || 0) + (u[k] || 0); });
    return a;
  }, {});
  const urgencyPie = Object.entries(urgencyTotals).map(([name, value]) => ({ name, value })).filter(d => d.value > 0);

  // ─── Type pie data ─────────────────────────────────────────────
  const typeTotals = (typeBreakdown || []).reduce((a, u) => {
    ['OneTime', 'FlowTask', 'Escalation', 'ConsultationApproval'].forEach(k => { a[k] = (a[k] || 0) + (u[k] || 0); });
    return a;
  }, {});
  const typePie = Object.entries(typeTotals).map(([name, value]) => ({ name, value })).filter(d => d.value > 0);

  // ─── Worth bar per user ────────────────────────────────────────
  const worthBars = uniqueUsers.map(u => ({
    user: u, Done: worthData[u]?.done || 0, Pending: worthData[u]?.pending || 0,
  }));

  // ─── Avg completion days bar ───────────────────────────────────
  const avgBars = uniqueUsers.filter(u => avgCompletionDays[u] != null).map(u => ({ user: u, days: avgCompletionDays[u] }));

  // ─── On-time vs late completion per user ───────────────────────
  const onTimeBars = uniqueUsers.map(u => ({
    user: u, 'On Time': worthData[u]?.onTime || 0, Late: worthData[u]?.lateCompleted || 0,
  }));

  const URGENCY_COLORS = { Urgent: '#ef4444', Important: '#f59e0b', Scheduled: '#3b82f6', 'Some Day': '#94a3b8' };
  const TYPE_COLORS = { OneTime: '#3b82f6', FlowTask: '#8b5cf6', Escalation: '#ef4444', ConsultationApproval: '#f59e0b' };

  return (
    <div className="dashboard-container">

      {/* ════════════ SYSTEM OVERVIEW ════════════ */}
      <section className="system-overview">
        <h3 className="dash-section-title">System Overview</h3>
        <div className="overview-grid">
          <OvCard icon={<FaCheckCircle />} bg="#dbeafe" fg="#2563eb" val={sys.completed} label="Completed" />
          <OvCard icon={<FaClock />} bg="#fef3c7" fg="#d97706" val={sys.pending} label="Pending" />
          <OvCard icon={<FaExclamationTriangle />} bg="#fee2e2" fg="#dc2626" val={sys.late} label="Late" />
          <OvCard icon={<FaTrophy />} bg="#d1fae5" fg="#059669" val={`${completionRate}%`} label="Completion" />
          <OvCard icon={<FaStar />} bg="#d1fae5" fg="#16a34a" val={sys.greenStars} label="Green Stars" />
          <OvCard icon={<FaStar />} bg="#fee2e2" fg="#ef4444" val={sys.redStars} label="Red Stars" />
          <OvCard icon={<FaBalanceScale />} bg="#ede9fe" fg="#7c3aed" val={totalWorthDone} label="Worth Done" />
          <OvCard icon={<FaFire />} bg="#fef3c7" fg="#d97706" val={totalWorthPending} label="Worth Pending" />
        </div>
      </section>

      {/* ════════════ STATUS TREND (area) ════════════ */}
      {(statusTrend || []).length > 1 && (
        <section className="chart-section">
          <h3 className="dash-section-title">Weekly Status Trend</h3>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={statusTrend} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <XAxis dataKey="week" fontSize={11} />
              <YAxis fontSize={11} />
              <Tooltip />
              <Legend />
              <Area type="monotone" dataKey="Completed" stroke="#10b981" fill="#10b981" fillOpacity={.25} />
              <Area type="monotone" dataKey="Pending" stroke="#f59e0b" fill="#f59e0b" fillOpacity={.2} />
              <Area type="monotone" dataKey="Late" stroke="#ef4444" fill="#ef4444" fillOpacity={.15} />
            </AreaChart>
          </ResponsiveContainer>
        </section>
      )}

      {/* ════════════ URGENCY + TYPE PIE ROW ════════════ */}
      <div className="pie-row">
        {urgencyPie.length > 0 && (
          <section className="chart-section pie-half">
            <h3 className="dash-section-title">By Urgency</h3>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={urgencyPie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {urgencyPie.map((d) => <Cell key={d.name} fill={URGENCY_COLORS[d.name] || '#94a3b8'} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </section>
        )}
        {typePie.length > 0 && (
          <section className="chart-section pie-half">
            <h3 className="dash-section-title">By Task Type</h3>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={typePie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {typePie.map((d) => <Cell key={d.name} fill={TYPE_COLORS[d.name] || '#94a3b8'} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </section>
        )}
        {(createdByBreakdown || []).length > 0 && (
          <section className="chart-section pie-half">
            <h3 className="dash-section-title">Tasks Created By</h3>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={createdByBreakdown} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75} label={({ name, value }) => `${name} (${value})`}>
                  {createdByBreakdown.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </section>
        )}
      </div>

      {/* ════════════ WORTH CHART ════════════ */}
      {worthBars.length > 0 && (
        <section className="chart-section">
          <h3 className="dash-section-title">Task Worth — Done vs Pending</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={worthBars} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <XAxis dataKey="user" fontSize={11} />
              <YAxis fontSize={11} />
              <Tooltip />
              <Legend />
              <Bar dataKey="Done" fill="#10b981" radius={[4,4,0,0]} />
              <Bar dataKey="Pending" fill="#f59e0b" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </section>
      )}

      {/* ════════════ ON-TIME vs LATE + AVG DAYS ROW ════════════ */}
      <div className="pie-row">
        {onTimeBars.length > 0 && (
          <section className="chart-section pie-half">
            <h3 className="dash-section-title">On-Time vs Late Completions</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={onTimeBars} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <XAxis dataKey="user" fontSize={11} />
                <YAxis fontSize={11} />
                <Tooltip />
                <Legend />
                <Bar dataKey="On Time" fill="#10b981" radius={[4,4,0,0]} />
                <Bar dataKey="Late" fill="#ef4444" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </section>
        )}
        {avgBars.length > 0 && (
          <section className="chart-section pie-half">
            <h3 className="dash-section-title">Avg. Completion Time (Days)</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={avgBars} layout="vertical" margin={{ top: 10, right: 20, left: 60, bottom: 0 }}>
                <XAxis type="number" fontSize={11} />
                <YAxis dataKey="user" type="category" fontSize={11} width={55} />
                <Tooltip />
                <Bar dataKey="days" fill="#8b5cf6" radius={[0,4,4,0]} />
              </BarChart>
            </ResponsiveContainer>
          </section>
        )}
      </div>

      {/* ════════════ PENALTY TREND (line) ════════════ */}
      {(lineData || []).length > 1 && (
        <section className="chart-section">
          <h3 className="dash-section-title">Penalty Trend (Red Stars / Week)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={lineData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <XAxis dataKey="week" fontSize={11} />
              <YAxis fontSize={11} />
              <Tooltip />
              <Legend />
              {uniqueUsers.map((user, idx) => (
                <Line key={user} type="monotone" dataKey={user} stroke={COLORS[idx % COLORS.length]} strokeWidth={2} dot={{ r: 3 }} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </section>
      )}

      {/* ════════════ WEEKLY BREAKDOWN BAR ════════════ */}
      {weeklyData.length > 0 && (
        <section className="chart-section">
          <h3 className="dash-section-title">Weekly Tasks per User</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={weeklyData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <XAxis dataKey="week" fontSize={11} />
              <YAxis fontSize={11} />
              <Tooltip />
              <Legend />
              {uniqueUsers.map((user, idx) => (
                <Bar key={`${user}-Completed`} dataKey={`${user}-Completed`} stackId={user} fill={COLORS[idx % COLORS.length]} />
              ))}
              {uniqueUsers.map((user, idx) => (
                <Bar key={`${user}-Late`} dataKey={`${user}-Late`} stackId={user} fill={`${COLORS[idx % COLORS.length]}55`} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </section>
      )}

      {/* ════════════ OVERDUE TASKS TABLE ════════════ */}
      {(overdueTasks || []).length > 0 && (
        <section className="chart-section">
          <h3 className="dash-section-title">
            <FaExclamationTriangle style={{ color: '#dc2626', marginRight: 6 }} />
            Top Overdue Tasks ({overdueTasks.length})
          </h3>
          <div className="overdue-table">
            <div className="ot-header">
              <span className="ot-h" style={{ flex: 3 }}>Task</span>
              <span className="ot-h" style={{ flex: 1.5 }}>Assignee</span>
              <span className="ot-h" style={{ flex: 1 }}>Urgency</span>
              <span className="ot-h" style={{ flex: 1 }}>Due Date</span>
              <span className="ot-h" style={{ flex: .7 }}>Overdue</span>
              <span className="ot-h" style={{ flex: .5 }}>Worth</span>
            </div>
            {overdueTasks.map(t => (
              <div key={t._id} className="ot-row">
                <span className="ot-cell" style={{ flex: 3, fontWeight: 600 }}>{t.title}</span>
                <span className="ot-cell" style={{ flex: 1.5 }}>{t.assignee}</span>
                <span className="ot-cell" style={{ flex: 1 }}>
                  <span className={`ot-urgency ${t.urgentLevel?.replace(/\s/g, '-')}`}>{t.urgentLevel}</span>
                </span>
                <span className="ot-cell" style={{ flex: 1 }}>{t.estimatedEndDate ? new Date(t.estimatedEndDate).toLocaleDateString('en-IN') : '—'}</span>
                <span className="ot-cell ot-overdue" style={{ flex: .7 }}>{t.overdueDays}d</span>
                <span className="ot-cell" style={{ flex: .5 }}>
                  {[...Array(t.totalWorth || 0)].map((_, i) => <FaStar key={i} style={{ color: '#16a34a', fontSize: 11 }} />)}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ════════════ INDIVIDUAL PERFORMANCE CARDS ════════════ */}
      <section className="performance-section">
        <h3 className="dash-section-title">Individual Performance & Rankings</h3>
        <div className="perf-grid">
          {userRankings.map((ur, idx) => {
            const perf = getPerf(ur);
            const isExpanded = expandedUsers[ur.user] || false;
            const uTasks = tasksPerUser[ur.user] || [];
            const cl = checklistSummary[ur.user];
            return (
              <div key={ur.user} className="perf-card">
                <div className="perf-rank" style={{ background: idx === 0 ? '#fef3c7' : idx === 1 ? '#f1f5f9' : idx === 2 ? '#fef3c7' : '#f8fafc' }}>
                  {idx < 3 && <FaTrophy style={{ color: idx === 0 ? '#d97706' : idx === 1 ? '#64748b' : '#b45309', fontSize: 12 }} />}
                  <span>#{idx + 1}</span>
                </div>

                <div className="perf-header" onClick={() => toggleExpand(ur.user)} style={{ cursor: 'pointer' }}>
                  <div className="perf-name">{ur.user}</div>
                  <span className="perf-badge" style={{ background: perf.bg, color: perf.color }}>{perf.label}</span>
                  <span style={{ marginLeft: 'auto', color: '#94a3b8' }}>{isExpanded ? <FaChevronUp /> : <FaChevronDown />}</span>
                </div>

                {/* Main stats */}
                <div className="perf-stats">
                  <PsItem val={ur.completed} lbl="Done" color="#059669" />
                  <PsItem val={ur.pending} lbl="Pend" color="#d97706" />
                  <PsItem val={ur.late} lbl="Late" color="#dc2626" />
                  <PsItem val={ur.greenStars} lbl={<FaStar style={{ fontSize: 10, color: '#16a34a' }} />} color="#059669" />
                  <PsItem val={ur.redStars} lbl={<FaStar style={{ fontSize: 10, color: '#ef4444' }} />} color="#dc2626" />
                  <PsItem val={`${ur.rate}%`} lbl="Rate" color="#2563eb" />
                </div>

                {/* Extended stats row */}
                <div className="perf-ext-stats">
                  <span className="pe-chip" title="Worth completed"><FaStar style={{ fontSize: 10, color: '#16a34a' }} /> {ur.done} pts done</span>
                  <span className="pe-chip" title="Worth pending"><FaClock style={{ fontSize: 10, color: '#d97706' }} /> {ur.pending} pts left</span>
                  {ur.avgDays != null && <span className="pe-chip" title="Avg completion time">~{ur.avgDays}d avg</span>}
                  <span className="pe-chip" title="On-time completions">{ur.onTime} on-time</span>
                  {ur.lateCompleted > 0 && <span className="pe-chip ot-late" title="Late completions">{ur.lateCompleted} late-done</span>}
                </div>

                {/* Checklist stats if available */}
                {cl && (
                  <div className="perf-ext-stats" style={{ marginTop: 4 }}>
                    <span className="pe-chip" style={{ background: '#ede9fe' }}>Checklists: {cl.total}</span>
                    <span className="pe-chip" style={{ background: '#d1fae5' }}>{cl.completions} completions</span>
                    {cl.redStars > 0 && <span className="pe-chip ot-late">{cl.redStars} CL penalties</span>}
                  </div>
                )}

                <div className="perf-progress-bar">
                  <div className="ppb-fill" style={{ width: `${ur.rate}%`, background: ur.rate >= 80 ? '#10b981' : ur.rate >= 50 ? '#f59e0b' : '#ef4444' }}></div>
                </div>

                {isExpanded && (
                  <div className="perf-tasks">
                    {loadingTasks ? <div style={{ padding: 8, color: '#6b7280', fontSize: 13 }}>Loading...</div> :
                      uTasks.length === 0 ? <div style={{ padding: 8, color: '#6b7280', fontSize: 13 }}>No active tasks</div> :
                      uTasks.filter(t => t.status !== 'completed').map(task => (
                        <div key={task._id} className="perf-task-row">
                          <div className="ptr-indicator" style={{ background: getStatusColor(task.status) }}></div>
                          <div className="ptr-content">
                            <div className="ptr-title">{task.title}</div>
                            <div className="ptr-meta">
                              <span className="ptr-status" style={{ color: getStatusColor(task.status) }}>{task.status}</span>
                              <span className="ptr-date">{task.estimatedEndDate ? `Due: ${new Date(task.estimatedEndDate).toLocaleDateString('en-IN')}` : ''}</span>
                            </div>
                          </div>
                          <div className="ptr-stars">
                            {(task.merit?.greenStars || 0) > 0 && <span style={{ color: '#16a34a' }}>{task.merit.greenStars}<FaStar style={{ fontSize: 10 }} /></span>}
                            {(task.merit?.redStars || 0) > 0 && <span style={{ color: '#ef4444' }}>{task.merit.redStars}<FaStar style={{ fontSize: 10 }} /></span>}
                          </div>
                        </div>
                      ))
                    }
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
};

// ── Small helper components ──────────────────────────────────────
function OvCard({ icon, bg, fg, val, label }) {
  return (
    <div className="overview-card">
      <div className="ov-icon" style={{ background: bg, color: fg }}>{icon}</div>
      <div className="ov-data">
        <div className="ov-value">{val}</div>
        <div className="ov-label">{label}</div>
      </div>
    </div>
  );
}

function PsItem({ val, lbl, color }) {
  return (
    <div className="ps-item">
      <span className="ps-val" style={{ color }}>{val}</span>
      <span className="ps-lbl">{lbl}</span>
    </div>
  );
}

export default MonthlyDashboard;
